import React, { useMemo } from "react";
import { styled } from "@mui/material";
import theme from "Theme/AppTheme";
import { AcxTranscriptionStore } from "../../Stores/AcxTranscriptionStore";
import { observer } from "mobx-react";
import { Blurb } from "./Blurb";
import { ITranscriptionSegment } from "services/TranscriptionService";

const ColoredSelectSpan = styled("span")(({ theme }) => ({
    /*** Works on common browsers ***/
    "::selection": {
        backgroundColor: theme.palette.primary[200],
    },

    /*** Mozilla based browsers ***/
    "::-moz-selection": {
        backgroundColor: theme.palette.primary[200],
    },
    /***For Other Browsers ***/
    "::-o-selection": {
        backgroundColor: theme.palette.primary[200],
    },

    "::-ms-selection": {
        backgroundColor: theme.palette.primary[200],
    },

    /*** For Webkit ***/
    "::-webkit-selection": {
        backgroundColor: theme.palette.primary[200],
    },
}));

const StyledSpan = React.memo(
    ({
        children,
        id,
        store,
        style,
    }: {
        children: React.ReactNode;
        id?: string;
        store: AcxTranscriptionStore;
        style?: React.CSSProperties;
    }) => {
        return (
            <ColoredSelectSpan
                style={style}
                id={id}
                onMouseDown={React.useCallback(
                    (e) => {
                        if (!id) return;
                        e.stopPropagation();
                        const startTimestamp = parseFloat(id.split("-")[0]);
                        const endTimestamp = parseFloat(id.split("-")[1]);
                        store.setSelectedStartTimestamp(startTimestamp);
                        store.setSelectedEndTimestamp(null);
                        store.setSelectedEndTimestampTemp(endTimestamp);
                    },
                    [id, store],
                )}
                onMouseUp={React.useCallback(
                    (e) => {
                        if (
                            !id ||
                            (!store.selectedStartTimestamp &&
                                store.selectedStartTimestamp !== 0)
                        )
                            return;
                        e.stopPropagation();
                        const thisWordStartTimestamp = parseFloat(
                            id.split("-")[0],
                        );
                        const endTimestamp = parseFloat(id.split("-")[1]);

                        if (
                            thisWordStartTimestamp ===
                                store.selectedStartTimestamp &&
                            store.selectingViaAudio
                        ) {
                            // Selected single word AND we WERE selecting via audio, so they just clicked away the existing highlight
                            store.setSelectedStartTimestamp(null);
                            store.setSelectedEndTimestamp(null);
                            store.setSelectedEndTimestampTemp(null);
                            store.setSelectingViaAudio(false);
                            return;
                        }

                        store.setSelectingViaAudio(false);

                        store.setSelectedEndTimestamp(endTimestamp);
                        // If end timestamp is less than start timestamp, use the temp end and this start
                        if (endTimestamp < store.selectedStartTimestamp) {
                            store.setSelectedStartTimestamp(
                                thisWordStartTimestamp,
                            );
                            store.setSelectedEndTimestamp(
                                store.selectedEndTimestampTemp,
                            );
                        }
                    },
                    [id, store],
                )}
            >
                {children}
            </ColoredSelectSpan>
        );
    },
);

export const BlurbText = observer(
    ({ blurb, store }: { blurb: Blurb; store: AcxTranscriptionStore }) => {
        // If no transcript words we can't do anything
        if (!store.transcriptWords) return null;

        // Memoize blurbWords and wordIndexMap for faster lookup
        const { blurbWords, wordIndexMap } = useMemo(() => {
            if (!store.transcriptWords)
                return { blurbWords: [], wordIndexMap: {} };
            const bw = store.transcriptWords
                .filter(
                    (w) =>
                        w.startTime >= blurb.timeInterval[0] - 0.001 &&
                        w.endTime <= blurb.timeInterval[1] + 0.001 &&
                        w.channel === blurb.channel,
                )
                .map((w) => ({
                    ...w,
                    text: w.text.trim(),
                }));

            // Build a map of word -> array of indexes for fast repeated lookup
            const map: Record<string, number[]> = {};
            bw.forEach((bwItem, idx) => {
                if (!map[bwItem.text]) map[bwItem.text] = [];
                map[bwItem.text].push(idx);
            });
            return { blurbWords: bw, wordIndexMap: map };
        }, [store.transcriptWords, blurb.timeInterval, blurb.channel]);

        const searchWordsRaw = useMemo(
            () =>
                store.searchString
                    .replace(/\s+/g, " ")
                    .trim()
                    .split(" ")
                    .filter(Boolean),
            [store.searchString],
        );
        const searchWords = useMemo(
            () => searchWordsRaw.map((w) => w.replace(/\s+/g, "")),
            [searchWordsRaw],
        );
        const searchLength = searchWords.length;
        const blurbTextWords = useMemo(
            () => blurb.text.split(" ").filter((word) => word.trim() !== ""),
            [blurb.text],
        );
        const highlighted: React.JSX.Element[] = [];
        let i = 0;
        const repeatedWordsMap: Record<string, number> = {};
        while (i < blurbTextWords.length) {
            /* Initialize variables for the current word */
            const loopIndex = i; // Capture i for use in closures
            // Get timestamp for this word
            const word = blurbTextWords[i];
            const wordIndices = wordIndexMap[word] || [];

            let w: ITranscriptionSegment;
            if (wordIndices.length <= 0) {
                i++;
                continue;
            }
            if (repeatedWordsMap[word] === undefined) {
                repeatedWordsMap[word] = 0;
            } else {
                repeatedWordsMap[word]++;
            }
            w = blurbWords[wordIndices[repeatedWordsMap[word] || 0]];

            if (w === undefined) {
                i++;
                continue;
            }

            let highlightStyle: React.CSSProperties = {
                color: "#000000",
                backgroundColor: "transparent",
            };

            /* Highlighting sections. The last edit to highlightStyle is what get's applied */
            /* Current priority is: (highest/last applied) Search > Audio Highlighting > Selection > Eddy > Adverse (lowest/first applied) */

            /* Highlighting for model tags (Eddy/Safety Events) */
            const isEddyWord =
                blurb.foundEddy &&
                store.eddyTimes.some(
                    (time) =>
                        w.startTime >= time.startTime &&
                        w.endTime <= time.endTime,
                );
            const isAdverseWord =
                blurb.foundAdverse &&
                store.adverseEventTimes.some(
                    (time) =>
                        w.startTime >= time.startTime &&
                        w.endTime <= time.endTime,
                );

            if (isAdverseWord) {
                highlightStyle.color =
                    theme.palette.highlight.adverseEvent.main;
                highlightStyle.fontWeight = theme.typography.fontWeightSemibold;
            }
            if (isEddyWord) {
                highlightStyle.color = theme.palette.highlight.eddy.main;
                highlightStyle.fontWeight = theme.typography.fontWeightSemibold;
            }

            /* Highlighting for audio playing */
            const wordDuration = w.endTime - w.startTime;
            if (
                w &&
                typeof store.audioStartTimestamp === "number" &&
                typeof store.audioEndTimestamp === "number" &&
                store.audioStartTimestamp - 0.001 <= w.startTime &&
                w.endTime - wordDuration <= store.audioEndTimestamp + 0.001 &&
                w.endTime >= store.audioStartTimestamp
            ) {
                highlightStyle.backgroundColor = theme.palette.primary[100];
            }

            /* Highlighting for selections */
            if (
                w &&
                typeof store.selectedStartTimestamp === "number" &&
                typeof store.selectedEndTimestamp === "number" &&
                store.selectedStartTimestamp - 0.001 <= w.startTime &&
                w.endTime <= store.selectedEndTimestamp + 0.001 &&
                w.endTime >= store.selectedStartTimestamp
            ) {
                highlightStyle.backgroundColor = theme.palette.primary[200];
            }

            /* Highlighting for search results (also where the actual spans are made) */
            let shouldSearchHighlight = false;
            let partialMatchIndices: number[] = [];
            // Multi-word partial match logic
            if (
                store.searchString.replace(/\s+/g, "").length > 2 &&
                store.searchString.trim() !== "" &&
                searchLength > 1 &&
                i + searchLength <= blurbTextWords.length
            ) {
                let allPartialMatch = true;
                for (let k = 0; k < searchLength; k++) {
                    const blurbWordNoSpace = blurbTextWords[i + k].replace(
                        /\s+/g,
                        "",
                    );
                    const searchWord = searchWords[k];
                    const searchWordRaw = searchWordsRaw[k];
                    if (/\s$/.test(searchWordRaw)) {
                        if (
                            blurbWordNoSpace.toLowerCase() !==
                            searchWord.toLowerCase()
                        ) {
                            allPartialMatch = false;
                            break;
                        } else {
                            partialMatchIndices[k] = 0;
                        }
                    } else {
                        const matchIdx = blurbWordNoSpace
                            .toLowerCase()
                            .indexOf(searchWord.toLowerCase());
                        if (matchIdx === -1) {
                            allPartialMatch = false;
                            break;
                        } else {
                            partialMatchIndices[k] = matchIdx;
                        }
                    }
                }
                if (allPartialMatch) {
                    shouldSearchHighlight = true;
                }
            } else {
                const blurbWordNoSpace = blurbTextWords[i].replace(/\s+/g, "");
                const searchWordRaw = store.searchString;
                const searchWord = store.searchString.replace(/\s+/g, "");
                if (/\s$/.test(searchWordRaw)) {
                    if (
                        blurbWordNoSpace.toLowerCase() ===
                            searchWord.toLowerCase() &&
                        store.searchString.replace(/\s+/g, "").length > 2 &&
                        store.searchString.trim() !== ""
                    ) {
                        shouldSearchHighlight = true;
                        partialMatchIndices[0] = 0;
                    }
                } else if (
                    blurbWordNoSpace
                        .toLowerCase()
                        .includes(searchWord.toLowerCase()) &&
                    store.searchString.replace(/\s+/g, "").length > 2 &&
                    store.searchString.trim() !== ""
                ) {
                    shouldSearchHighlight = true;
                    partialMatchIndices[0] = blurbWordNoSpace
                        .toLowerCase()
                        .indexOf(searchWord.toLowerCase());
                }
            }

            if (shouldSearchHighlight && searchLength > 1) {
                // Multi-word partial highlight
                const phraseWords = blurbTextWords.slice(i, i + searchLength);
                // Use repeatedWordsMap for each word in phrase
                const phraseIds = phraseWords.map((phraseWord) => {
                    const phraseWordIndices = wordIndexMap[phraseWord] || [];
                    let phraseW: ITranscriptionSegment | undefined;
                    if (phraseWordIndices.length > 0) {
                        if (repeatedWordsMap[phraseWord] === undefined)
                            repeatedWordsMap[phraseWord] = 0;
                        else repeatedWordsMap[phraseWord]++;
                        phraseW =
                            blurbWords[
                                phraseWordIndices[
                                    repeatedWordsMap[phraseWord] || 0
                                ]
                            ];
                    }
                    return phraseW
                        ? `${phraseW.startTime}-${phraseW.endTime}`
                        : undefined;
                });
                const phraseSpans = phraseWords.map((word, idx) => {
                    const searchWord = searchWords[idx];
                    const searchWordRaw = searchWordsRaw[idx];
                    const blurbWordNoSpace = word.replace(/\s+/g, "");
                    const matchIdx = partialMatchIndices[idx];
                    // Use phraseW for highlightStyle
                    let phraseW: ITranscriptionSegment | undefined;
                    const phraseWordIndices = wordIndexMap[word] || [];
                    if (phraseWordIndices.length > 0) {
                        phraseW =
                            blurbWords[
                                phraseWordIndices[repeatedWordsMap[word] || 0]
                            ];
                    }
                    let phraseHighlightStyle: React.CSSProperties = {};
                    if (
                        phraseW &&
                        typeof store.selectedStartTimestamp === "number" &&
                        typeof store.selectedEndTimestamp === "number" &&
                        store.selectedStartTimestamp <= phraseW.startTime &&
                        phraseW.endTime <= store.selectedEndTimestamp
                    ) {
                        phraseHighlightStyle.backgroundColor =
                            theme.palette.primary[100];
                        phraseHighlightStyle.color = "#000000";
                    }
                    if (typeof matchIdx === "number" && matchIdx !== -1) {
                        if (/\s$/.test(searchWordRaw)) {
                            return (
                                <StyledSpan
                                    key={loopIndex + "-" + idx}
                                    id={phraseIds[idx]}
                                    store={store}
                                    style={phraseHighlightStyle}
                                >
                                    <span
                                        style={{
                                            color: theme.palette.primary.main,
                                            fontWeight: 600,
                                        }}
                                    >
                                        {word}
                                    </span>{" "}
                                </StyledSpan>
                            );
                        }
                        return (
                            <StyledSpan
                                key={loopIndex + "-" + idx}
                                id={phraseIds[idx]}
                                store={store}
                                style={phraseHighlightStyle}
                            >
                                {blurbWordNoSpace.substring(0, matchIdx)}
                                <span
                                    style={{
                                        color: theme.palette.primary.main,
                                        fontWeight: 600,
                                    }}
                                >
                                    {blurbWordNoSpace.substring(
                                        matchIdx,
                                        matchIdx + searchWord.length,
                                    )}
                                </span>
                                {blurbWordNoSpace.substring(
                                    matchIdx + searchWord.length,
                                )}{" "}
                            </StyledSpan>
                        );
                    }
                    return (
                        <StyledSpan
                            key={loopIndex + "-" + idx}
                            id={phraseIds[idx]}
                            store={store}
                            style={phraseHighlightStyle}
                        >
                            {word}{" "}
                        </StyledSpan>
                    );
                });
                highlighted.push(...phraseSpans);
                i += searchLength;
                continue;
            } else if (shouldSearchHighlight) {
                // Single-word partial highlight
                const word = blurbTextWords[i];
                const blurbWordNoSpace = word.replace(/\s+/g, "");
                const searchWordRaw = store.searchString;
                const searchWord = store.searchString.replace(/\s+/g, "");
                const matchIdx = partialMatchIndices[0];
                const w = blurbWords[wordIndices[repeatedWordsMap[word] || 0]];
                if (typeof matchIdx === "number" && matchIdx !== -1) {
                    if (/\s$/.test(searchWordRaw)) {
                        highlighted.push(
                            <StyledSpan
                                store={store}
                                key={loopIndex}
                                id={
                                    w
                                        ? `${w.startTime}-${w.endTime}`
                                        : undefined
                                }
                                style={highlightStyle}
                            >
                                <span
                                    style={{
                                        color: theme.palette.primary.main,
                                        fontWeight: 600,
                                    }}
                                >
                                    {word}
                                </span>
                                {loopIndex < blurbTextWords.length - 1 && " "}
                            </StyledSpan>,
                        );
                    } else {
                        highlighted.push(
                            <StyledSpan
                                key={loopIndex}
                                store={store}
                                id={
                                    w
                                        ? `${w.startTime}-${w.endTime}`
                                        : undefined
                                }
                                style={highlightStyle}
                            >
                                {blurbWordNoSpace.substring(0, matchIdx)}
                                <span
                                    style={{
                                        color: theme.palette.primary.main,
                                        fontWeight: 600,
                                    }}
                                >
                                    {blurbWordNoSpace.substring(
                                        matchIdx,
                                        matchIdx + searchWord.length,
                                    )}
                                </span>
                                {blurbWordNoSpace.substring(
                                    matchIdx + searchWord.length,
                                )}
                                {loopIndex < blurbTextWords.length - 1 && " "}
                            </StyledSpan>,
                        );
                    }
                } else {
                    highlighted.push(
                        <StyledSpan
                            key={loopIndex}
                            store={store}
                            id={w ? `${w.startTime}-${w.endTime}` : undefined}
                            style={highlightStyle}
                        >
                            {word}
                            {loopIndex < blurbTextWords.length - 1 && " "}
                        </StyledSpan>,
                    );
                }
            } else {
                // No highlight
                highlighted.push(
                    <StyledSpan
                        key={loopIndex}
                        id={w ? `${w.startTime}-${w.endTime}` : undefined}
                        store={store}
                        style={highlightStyle}
                    >
                        {word}
                        {loopIndex < blurbTextWords.length - 1 && " "}
                    </StyledSpan>,
                );
            }
            i++;
        }

        /* Wrap all the spans in a parent with blurb id */
        return (
            <StyledSpan
                id={
                    "blurb-" +
                    blurb.timeInterval[0].toString() +
                    "-" +
                    blurb.timeInterval[1].toString() +
                    "-blurb"
                }
                store={store}
            >
                {highlighted}
            </StyledSpan>
        );
    },
);
